import React from 'react';
import FeatureMockupCard from './FeatureMockupCard';
import {
  AISuggestReplyMockup, OfferThreadMockup, ViewingBookingMockup,
  ShareCardMockup, EncryptedChatMockup, VerifiedBerMockup,
  PprPriceMockup, PassportMockup, HonestReadMockup,
  NegotiationMockup, PriceAlertMockup, DesignRoomMockup,
  PwaInstallMockup, DirectEnquiryMockup, RealPriceIndexMockup,
  OpenBiddingMockup,
  marketMockupCopy,
} from './featureMockups';
import { useMarket } from '../hooks/useMarket';

/**
 * Market-aware feature grid. Builds the AGENT / BUYER feature copy from the
 * active market so a UK visitor sees £, "EPC", "HM Land Registry" and "Tube"
 * references (and never "Luas" / "SEAI" / "Eircode"). All neighbourhood /
 * regulator / register references are pulled from `marketMockupCopy(market)`
 * so they can never drift out of sync with the mockups.
 */

const savingsAvg = (market) => {
  // Average buyer saving on Negotiation IQ — keeps the same £/€/$ prefix as
  // the rest of the market copy. The absolute number stays constant because
  // it's a marketing figure, not a live calculation.
  const c = marketMockupCopy(market);
  return c.fmt(7200);
};

const priceRegulatorCredit = (market) => {
  const c = marketMockupCopy(market);
  // Where the price intelligence data comes from, per market.
  if (c.isUK) return 'HM Land Registry Price Paid Data';
  if (c.isUS) return 'MLS + county public records';
  return 'Property Price Register (PPR)';
};

const stylesCopy = (market) => {
  // The three named styles listed under "Design My Room".
  const c = marketMockupCopy(market);
  if (c.isUK) return 'Six styles including Victorian, Modern British and Coastal';
  if (c.isUS) return 'Six styles including Mid-century, Farmhouse and Coastal';
  return 'Six Irish-relevant styles';
};

const homesLine = (market) => {
  // "Search Irish homes …" — swap for the active market.
  if (market.code === 'GB') return 'Search UK homes from your home-screen.';
  if (market.code === 'US') return 'Search US homes from your home-screen.';
  return 'Search Irish homes from your home-screen.';
};

const registryDescription = (market) => {
  const c = marketMockupCopy(market);
  if (c.isUK) return 'Not "asking price" — actual sold prices, refreshed monthly from HM Land Registry, plotted by borough and postcode. Spot a soft market before the agent does.';
  if (c.isUS) return 'Not "list price" — actual sold prices, refreshed monthly from county records and MLS, plotted by metro and ZIP. Spot a soft market before the agent does.';
  return 'Not "median list price" — actual sold prices, refreshed monthly from the official register, plotted by county and area. Spot a soft market before the agent does.';
};

const priceIndexTitle = (market) => {
  const c = marketMockupCopy(market);
  return `The truth about prices, by ${c.postcodeLabel}.`;
};

const registerRefreshBullet = (market) => {
  const c = marketMockupCopy(market);
  return `Official ${c.registerLabel} · refreshed monthly`;
};

const geographyBullet = (market) => {
  const c = marketMockupCopy(market);
  if (c.isUK) return 'Borough + postcode breakdown';
  if (c.isUS) return 'Metro + ZIP breakdown';
  return 'County + area breakdown';
};

const chatLocalityLine = (market) => {
  const c = marketMockupCopy(market);
  if (c.isUK) return 'Direct chat with the listing agent. Send a real offer with conditions. Pick a viewing slot. Add it to your calendar. All encrypted. UK-hosted, GDPR-aligned.';
  if (c.isUS) return 'Direct chat with the listing agent. Send a real offer with conditions. Pick a viewing slot. Add it to your calendar. All encrypted. US-hosted, TCPA-aligned.';
  return 'Direct chat with the listing agent. Send a real offer with conditions. Pick a viewing slot. Add it to your calendar. All encrypted. None of it leaves Ireland.';
};

const openBiddingCompliance = (market) => {
  const c = marketMockupCopy(market);
  // Regulator name for the vendor-consent audit-log bullet
  if (c.isUK) return 'Estate Agents Act 1979-aligned · vendor-consent audit log';
  if (c.isUS) return 'State licensing-compliant · vendor-consent audit log';
  return 'PSRA-aligned · vendor-consent audit log';
};

/**
 * Builds the AGENT feature list against the active market. Static copy that
 * never mentions a specific country stays the same across markets; the rest
 * is composed from the market profile.
 */
const buildAgentFeatures = (market) => {
  const c = marketMockupCopy(market);
  const marketName = market.isUK ? 'the UK' : market.isUS ? 'the US' : 'Ireland';
  return [
    {
      key: 'ai-suggest-replies',
      eyebrow: 'AI Suggest Replies',
      title: 'GPT writes your first reply. You hit send.',
      body: 'Tap ✨ and get three on-brand replies tuned to the buyer\'s last message — varied between direct, viewing-booking and qualifying. Cuts average reply time from hours to seconds.',
      bullets: ['Our AI + your listing context', 'Three suggestions, one tap to send', 'Only you see them — buyers never know'],
      mockup: <AISuggestReplyMockup market={market} />,
      accent: 'purple',
      firstBadge: marketName,
      ctaLabel: 'See it in chat',
      ctaTo: '/inbox',
    },
    {
      key: 'open-bidding',
      eyebrow: 'Open Bidding · Vendor-controlled',
      title: 'Turn rival buyers into rival bidders.',
      body: `Flip a switch and every buyer in chat sees an anonymised "current highest ${c.currencySymbol}X · N active bidders" banner. The moment one outbids the others, everyone gets pushed and rushes back to counter. Higher final prices, faster sales. Off by default — you (and the vendor) decide.`,
      bullets: ['Anonymised — no buyer names ever shared', 'Per-property toggle, instant revert', openBiddingCompliance(market)],
      mockup: <OpenBiddingMockup market={market} />,
      accent: 'amber',
      firstBadge: marketName,
      ctaLabel: 'Enable on a listing',
      ctaTo: '/seller-dashboard',
    },
    {
      key: 'offers-viewings',
      eyebrow: 'Offers + viewings, in-thread',
      title: 'Close deals without leaving the conversation.',
      body: 'Buyers send proper offers in chat. You Accept, Decline or Counter. Propose up to 5 viewing slots — picked slots auto-generate an .ics invite for Apple/Google/Outlook calendars.',
      bullets: ['Status-tracked offer cards (no spreadsheet)', '.ics calendar invites included', 'Push notification the moment they decide'],
      mockup: <OfferThreadMockup market={market} />,
      accent: 'emerald',
      firstBadge: marketName,
      ctaLabel: 'How it works',
      ctaTo: '/list-with-us',
    },
    {
      key: 'share-cards',
      eyebrow: 'Dynamic share cards',
      title: 'Every listing is a viral asset, not a URL.',
      body: 'When buyers paste your listing into WhatsApp / iMessage / Twitter, they see a branded preview with price, photo and badge. One-click export to 1080×1920 for Instagram Stories.',
      bullets: ['1200×630 rich link previews', '1080×1920 Story export', 'Share to Instagram in one tap'],
      mockup: <ShareCardMockup market={market} />,
      accent: 'fuchsia',
      firstBadge: marketName,
      ctaLabel: 'Preview a card',
      ctaTo: '/seller-dashboard',
    },
    {
      key: 'viewing-scheduling',
      eyebrow: 'Calendar-aware viewings',
      title: 'Stop the email tag for viewing slots.',
      body: 'Offer five times in a single tap. The buyer picks one. Both sides get a calendar invite. Reminders are baked in. Your no-shows drop because everyone has it on their phone.',
      bullets: ['No-show rate ↓ measurably', 'Works on iOS, Android, Outlook, Gmail', 'Native push when picked'],
      mockup: <ViewingBookingMockup />,
      accent: 'amber',
      ctaLabel: 'See the flow',
      ctaTo: '/list-with-us',
    },
    {
      key: 'encrypted-chat',
      eyebrow: 'Encrypted at rest',
      title: 'AES-256 chat. GDPR-grade by default.',
      body: 'Every message and offer is encrypted before it touches the database. Even our staff can\'t read your client conversations. AI moderation flags abuse or off-platform attempts.',
      bullets: ['AES-256 at rest, TLS in transit', 'GDPR-aligned record-keeping', 'Compliance-ready audit trail'],
      mockup: <EncryptedChatMockup />,
      accent: 'indigo',
      ctaLabel: 'Privacy & security',
      ctaTo: '/privacy',
    },
    {
      key: 'verified-energy',
      eyebrow: `${c.energyLabel} verification`,
      title: 'Verified rating, not a marketing claim.',
      body: `We cross-check every listing against the live ${c.registerRegulator} register. Verified listings get a real badge — better buyers, fewer time-wasters, more credible portfolio.`,
      bullets: [`Pulled from official ${c.registerRegulator} register`, 'Shown front-and-centre on every card', 'A real moat against fake claims'],
      mockup: <VerifiedBerMockup market={market} />,
      accent: 'emerald',
      ctaLabel: 'How vetting works',
      ctaTo: '/how-we-vet-listings',
    },
    {
      key: 'price-intel',
      eyebrow: `${c.registerLabel} price intelligence`,
      title: 'Buyers arrive calibrated. Your job got easier.',
      body: 'Every listing shows the actual median sold price for the road, area and county. No more "what did the neighbour\'s house go for?" — the data is right there.',
      bullets: [`Live ${priceRegulatorCredit(market)}`, '200+ areas', 'Reduces lowball offers'],
      mockup: <PprPriceMockup market={market} />,
      accent: 'cyan',
      ctaLabel: 'Browse the index',
      ctaTo: '/price-index',
    },
    {
      key: 'direct-enquiries',
      eyebrow: 'Direct enquiries',
      title: 'Buyer → you. Nobody in between.',
      body: 'No buyer funnel, no lead-resale, no "let me transfer you". Email lands straight in your inbox. Reply, book a viewing, done. The way it should have always worked.',
      bullets: ['Zero lead-resale', 'Avg. response time: 8 minutes', 'Your client list stays your client list'],
      mockup: <DirectEnquiryMockup />,
      accent: 'emerald',
      ctaLabel: 'Claim your 30 days',
      ctaTo: '/list-with-us',
    },
    {
      key: 'passport',
      eyebrow: 'Property Passport',
      title: 'eIDAS-compliant. Travels with the house.',
      body: `Every sale gets a tamper-evident bundle — ${c.energyLabel}, title deed, e-signatures — that stays with the property forever. The next buyer trusts it instantly. The next sale is shorter.`,
      bullets: ['eIDAS-aligned e-signatures', 'Tamper-evident audit chain', 'Cuts due-diligence from weeks to days'],
      mockup: <PassportMockup market={market} />,
      accent: 'indigo',
      firstBadge: marketName,
      ctaLabel: 'See a sample',
      ctaTo: '/passport/sample',
    },
  ];
};

/**
 * Builds the BUYER feature list against the active market.
 */
const buildBuyerFeatures = (market) => {
  const c = marketMockupCopy(market);
  const marketName = market.isUK ? 'the UK' : market.isUS ? 'the US' : 'Ireland';
  return [
    {
      key: 'honest-read',
      eyebrow: 'Honest Read',
      title: 'The plain-English listing decoder.',
      body: `AI reads the agent copy and tells you what's actually true. "Walk to ${c.exampleTransit}" is 22 min. "Sunny garden" faces north. "Briefly available" was first listed 87 days ago. One score, plain English.`,
      bullets: [`Cross-checked against street, ${c.energyLabel}, ${c.registerLabel}`, '0 → 5 honesty score', 'No fluff, no euphemism'],
      mockup: <HonestReadMockup market={market} />,
      accent: 'rose',
      firstBadge: marketName,
      ctaLabel: 'Try Honest Read',
      ctaTo: '/listings',
    },
    {
      key: 'open-bidding-buyer',
      eyebrow: 'Open Bidding — when the seller allows it',
      title: 'See the real number you need to beat.',
      body: 'On homes where the seller has opted in, you stop bidding blind. A small banner in the chat tells you the current highest offer (anonymously) and how many other buyers are active. Outbid by someone? You\'ll know within seconds — push notification, with a "Counter now" button.',
      bullets: ['Anonymised — no rival buyer names shown', 'Push the moment you\'re outbid', 'Stops "phantom bidder" tactics'],
      mockup: <OpenBiddingMockup market={market} />,
      accent: 'amber',
      firstBadge: marketName,
      ctaLabel: 'How it works',
      ctaTo: '/how-we-vet-listings',
    },
    {
      key: 'negotiation-iq',
      eyebrow: 'Negotiation IQ',
      title: 'Walk in with the right number.',
      body: `We pull the last 24 months of real sold prices on the road, factor in time of year, condition and ${c.energyLabel}. You get an opening bid and a ceiling. Buyers using it save ${savingsAvg(market)} on average.`,
      bullets: [`Live ${c.registerLabel} + ${c.postcodeLabel} data`, `Avg. saved ${savingsAvg(market)} per purchase`, 'Plain-English playbook'],
      mockup: <NegotiationMockup market={market} />,
      accent: 'amber',
      firstBadge: marketName,
      ctaLabel: 'How it calculates',
      ctaTo: '/ai-assistant',
    },
    {
      key: 'chat-with-agents',
      eyebrow: 'Encrypted chat with the agent',
      title: 'Make an offer in two taps. No email tag.',
      body: chatLocalityLine(market),
      bullets: ['AES-256 encrypted at rest', 'Offers + viewings in-thread', 'Calendar invites built in'],
      mockup: <OfferThreadMockup market={market} />,
      accent: 'emerald',
      ctaLabel: 'See a sample chat',
      ctaTo: '/inbox',
    },
    {
      key: 'verified-listings',
      eyebrow: 'Verified listings',
      title: `The ${c.energyLabel} is real. The price is calibrated.`,
      body: `We cross-check every ${c.energyLabel} against the ${c.registerRegulator} register and show the real median sold price next to every listing. No "${c.energyGrade} (marketing)". No "guide price" mind games.`,
      bullets: [`Live ${c.registerRegulator} register check`, `${c.registerLabel} median on every card`, 'Verified badge — earned, not claimed'],
      mockup: <VerifiedBerMockup market={market} />,
      accent: 'cyan',
      ctaLabel: 'How vetting works',
      ctaTo: '/how-we-vet-listings',
    },
    {
      key: 'price-alerts',
      eyebrow: 'Live price-drop alerts',
      title: 'The exact second the price drops, you know.',
      body: 'Save a search. Get lock-screen push notifications when matching listings drop in price or new ones land. Works on the home-screen-installed PWA — feels like a native app.',
      bullets: ['Lock-screen push (iOS + Android)', 'New-listing + price-drop alerts', 'Works offline'],
      mockup: <PriceAlertMockup market={market} />,
      accent: 'purple',
      ctaLabel: 'Start a saved search',
      ctaTo: '/listings',
    },
    {
      key: 'design-room',
      eyebrow: 'Design My Room · AI',
      title: 'See yourself in the space before you bid.',
      body: `Snap a phone photo of the current room. Pick a style — six on offer — and watch the same room transform. Three free renders a day.`,
      bullets: ['Powered by our AI image engine', stylesCopy(market), '3 free renders / day'],
      mockup: <DesignRoomMockup />,
      accent: 'fuchsia',
      firstBadge: marketName,
      ctaLabel: 'Re-design a room',
      ctaTo: '/design-my-room',
    },
    {
      key: 'install-pwa',
      eyebrow: 'Install on your phone',
      title: homesLine(market),
      body: 'Tap "Add to Home Screen" and lovinahome becomes a real app — full-screen, fast, push-enabled, works offline. No app-store install, no 250MB download.',
      bullets: ['iOS + Android home-screen', 'Push for saved searches', 'Offline browse of recent listings'],
      mockup: <PwaInstallMockup />,
      accent: 'sky',
      ctaLabel: 'Install lovinahome',
      ctaTo: '/',
    },
    {
      key: 'real-price-index',
      eyebrow: 'Real Price Index',
      title: priceIndexTitle(market),
      body: registryDescription(market),
      bullets: [registerRefreshBullet(market), geographyBullet(market), 'YoY growth, distribution, outliers'],
      mockup: <RealPriceIndexMockup market={market} />,
      accent: 'sky',
      ctaLabel: 'See the index',
      ctaTo: '/price-index',
    },
  ];
};

/**
 * Renders the audience-specific feature card grid.
 *
 *   <FeatureShowcaseGrid audience="agent" />
 *   <FeatureShowcaseGrid audience="buyer" countryOverride="United Kingdom" />
 */
const FeatureShowcaseGrid = ({ audience = 'buyer', limit, countryOverride }) => {
  const market = useMarket(countryOverride);
  const features = audience === 'agent' ? buildAgentFeatures(market) : buildBuyerFeatures(market);
  const items = typeof limit === 'number' ? features.slice(0, limit) : features;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((f) => (
        <FeatureMockupCard
          key={f.key}
          eyebrow={f.eyebrow}
          title={f.title}
          body={f.body}
          bullets={f.bullets}
          mockup={f.mockup}
          accent={f.accent}
          firstBadge={f.firstBadge || ''}
          ctaLabel={f.ctaLabel}
          ctaTo={market.withPrefix(f.ctaTo)}
          testId={`feature-card-${f.key}`}
        />
      ))}
    </div>
  );
};

export default FeatureShowcaseGrid;
